/**
 * Authenticated shell: green sidebar + main content area.
 *
 * Layout mirrors lovable-mock/AppLayout: full-height flex row, sidebar on
 * the left, main content fills the rest. A slim top-bar inside the main
 * column shows the tenant identity (logo / name) and any active flags.
 *
 * The contract-editor routes (legacy /edit and IR /beta) get the full
 * column and set their own widths (lib/editor-layout.ts: chrome at 1400px,
 * the Avtal tab wider on large monitors); the Översikt route drops the padding + max-width
 * entirely so its portfolio band can run full-bleed (ADR-0081) — the page
 * re-applies the standard container below the band itself; other routes
 * keep a comfortable max-width.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useActiveGroup } from '../active-group-context.js';
import { useTenantSession } from '../auth-context.js';
import { useBranding } from '../branding-context.js';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { NewLayoutBanner } from '../components/DocumentEngine.js';
import { NotificationsBell } from '../components/NotificationsBell.js';
import { Sidebar } from '../components/Sidebar.js';

export function Shell() {
  const { token, actor, error } = useTenantSession();
  const { branding } = useBranding();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const topRef = useStickyTopVar();
  const isEditor = /^\/contracts\/[^/]+\/(edit|beta)(\/|$)/.test(location.pathname);
  const isOverview = location.pathname === '/';
  const innerClass = isEditor
    ? 'w-full'
    : isOverview
      ? 'w-full'
      : 'mx-auto w-full max-w-5xl';

  return (
    <div className="flex min-h-screen w-full items-start bg-white">
      <Sidebar mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div ref={topRef} className="sticky top-0 z-10">
        <header className="flex h-14 items-center gap-3 border-b border-stone-200 bg-white px-3 sm:px-6">
          {/* Hamburger — visible only below lg, the threshold at which the
              persistent sidebar takes over. */}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Öppna meny"
            className="-ml-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-stone-600 hover:bg-stone-100 hover:text-stone-900 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1 lg:flex-initial">
            <TenantChip
              logoUrl={branding?.tenant.brandLogoOnWhiteUrl ?? null}
              tenantName={branding?.tenant.name ?? ''}
              role={actor?.tenantRole}
            />
          </div>
          {/* Active-group + notifications pill. Centered on lg+ via
              `lg:mx-auto`; right-aligned on smaller widths. */}
          <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50/70 px-1.5 py-1 shadow-[0_1px_0_rgba(0,0,0,0.02),0_4px_12px_-6px_rgba(0,0,0,0.08)] lg:mx-auto">
            <ActiveGroupChip />
            {token && (
              <>
                <span className="h-5 w-px bg-stone-200" aria-hidden="true" />
                <NotificationsBell />
              </>
            )}
          </div>
          {/* Reserved right cell — keeps the centre pill visually balanced on lg+. */}
          <div className="hidden lg:block lg:flex-1" />
        </header>
        <NewLayoutBanner />
        </div>
        <main className={isOverview ? 'bg-white' : 'bg-white p-3 sm:p-6'}>
          {error && (
            <div
              className={`${isOverview ? 'm-3 sm:m-6 ' : ''}mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700`}
            >
              {error}
            </div>
          )}
          <div className={innerClass}>
            {!token ? (
              <div className="text-sm text-stone-400">loading…</div>
            ) : (
              <ErrorBoundary resetKeys={[location.pathname]}>
                <Outlet />
              </ErrorBoundary>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

/** Publishes the sticky top stack's height (header + banner) as
 *  `--shell-top`, so sticky panes below can offset themselves. */
function useStickyTopVar() {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty('--shell-top', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--shell-top');
    };
  }, []);
  return ref;
}

function TenantChip({
  logoUrl,
  tenantName,
  role,
}: {
  logoUrl: string | null;
  tenantName: string;
  role: string | undefined;
}) {
  return (
    <div className="flex items-center gap-3">
      {logoUrl ? (
        <img src={logoUrl} alt="" className="max-h-8 max-w-[170px] object-contain" />
      ) : (
        <span className="text-sm font-medium text-stone-700">{tenantName}</span>
      )}
      {role && (
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-stone-500">
          {role}
        </span>
      )}
    </div>
  );
}

function ActiveGroupChip() {
  const { writeableGroups, activeGroupId, setActiveGroupId, loading } =
    useActiveGroup();

  if (loading || writeableGroups === null) return null;
  if (writeableGroups.length === 0) {
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Ingen grupp tilldelad
      </span>
    );
  }

  const active = writeableGroups.find((g) => g.id === activeGroupId);

  if (writeableGroups.length === 1) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-0.5 text-[11px]">
        <span className="text-stone-400 uppercase tracking-wider text-[10px]">
          Grupp
        </span>
        <span className="font-medium text-stone-800">
          {active?.name ?? writeableGroups[0]!.name}
        </span>
      </div>
    );
  }

  return (
    <label className="group relative flex cursor-pointer items-center gap-2 rounded-full pl-2.5 pr-7 py-1 text-[11px] hover:bg-white">
      <span className="text-stone-400 uppercase tracking-wider text-[10px]">
        Aktiv grupp
      </span>
      <span className="font-medium text-stone-800 truncate max-w-[14rem]">
        {active?.name ?? writeableGroups[0]!.name}
      </span>
      <svg
        viewBox="0 0 12 12"
        aria-hidden="true"
        className="pointer-events-none absolute right-2 h-3 w-3 text-stone-500 transition-transform group-hover:translate-y-px"
      >
        <path
          d="M3 4.5L6 7.5L9 4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <select
        value={activeGroupId ?? ''}
        onChange={(e) => setActiveGroupId(e.target.value)}
        aria-label="Aktiv grupp"
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {writeableGroups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
    </label>
  );
}
